import type { ComponentType } from "react";

import { ConsumptionGranularity, EnergyType, PointUnit } from "@eisox/api/core-api";
import {
  BarChartIcon,
  HeatIcon,
  SubstationIcon,
  ThermometerIcon,
  WaterDropIcon,
  ZapIcon,
  type IconProps,
} from "@eisox/icons";
import { colors } from "@eisox/tokens";

import { DATE_FORMAT } from "~/constants/date.constant";
import { m } from "~/paraglide/messages";

import {
  Comparison,
  ConsumptionTab,
  PeriodPreset,
  SeriesKey,
  type CalendarSpan,
  type ChartSeries,
  type ComparisonSettings,
  type GranularitySettings,
  type PresetSettings,
} from "./types/consumption.type";

const CONSUMPTION_TABS = [
  { tab: ConsumptionTab.CHART, Icon: BarChartIcon, label: m.consumption_tab_chart },
  { tab: ConsumptionTab.HIERARCHY, Icon: SubstationIcon, label: m.consumption_tab_hierarchy },
];

const ENERGIES: Record<EnergyType, { label: () => string; Icon: ComponentType<IconProps> }> = {
  [EnergyType.ELECTRICITY]: { label: m.consumption_energy_electricity, Icon: ZapIcon },
  [EnergyType.GAS]: { label: m.consumption_energy_gas, Icon: HeatIcon },
  [EnergyType.WATER]: { label: m.consumption_energy_water, Icon: WaterDropIcon },
  [EnergyType.HEAT]: { label: m.consumption_energy_heat, Icon: ThermometerIcon },
};

const ENERGY_ORDER = [EnergyType.ELECTRICITY, EnergyType.GAS, EnergyType.WATER, EnergyType.HEAT];

const UNIT_SYMBOLS: Record<PointUnit, string | undefined> = {
  [PointUnit.CUBIC_METER]: "m³",
  [PointUnit.CUBIC_METER_PER_HOUR]: "m³/h",
  [PointUnit.KILOWATT]: "kW",
  [PointUnit.KILOWATT_HOUR]: "kWh",
  [PointUnit.DEGREE_CELSIUS]: "°C",
  [PointUnit.HOUR]: "h",
  [PointUnit.BOOLEAN]: undefined,
  [PointUnit.COEFFICIENT]: undefined,
  [PointUnit.DATE]: undefined,
  [PointUnit.NUMBER]: undefined,
  [PointUnit.STRING]: undefined,
  [PointUnit.TIMESTAMP]: undefined,
};

const { MINUTE, HOUR, DAY, MONTH, YEAR } = ConsumptionGranularity;

const GRANULARITIES: Record<ConsumptionGranularity, GranularitySettings> = {
  [MINUTE]: {
    unit: "minute",
    label: m.consumption_view_minute,
    averageLabel: m.consumption_average_per_minute,
    count: count => m.minute({ count }),
    bucketFormat: "L LT",
    tickFormat: "DD/MM LT",
    slotFormat: "YYYY-MM-DDTHH:mm",
    calendarUnit: "day",
    maxRange: { amount: 1, unit: "day" },
    retention: { amount: 90, unit: "day" },
  },
  [HOUR]: {
    unit: "hour",
    label: m.consumption_view_hour,
    averageLabel: m.consumption_average_per_hour,
    count: count => m.hour({ count }),
    bucketFormat: "L LT",
    tickFormat: "DD/MM LT",
    slotFormat: "YYYY-MM-DDTHH",
    calendarUnit: "day",
    maxRange: { amount: 1, unit: "month" },
    retention: { amount: 90, unit: "day" },
  },
  [DAY]: {
    unit: "day",
    label: m.consumption_view_day,
    averageLabel: m.consumption_average_per_day,
    count: count => m.day({ count }),
    bucketFormat: "L",
    tickFormat: "DD/MM",
    slotFormat: DATE_FORMAT,
    calendarUnit: "day",
    maxRange: { amount: 1, unit: "year" },
    retention: { amount: 1, unit: "year" },
  },
  [MONTH]: {
    unit: "month",
    label: m.consumption_view_month,
    averageLabel: m.consumption_average_per_month,
    count: count => m.month({ count }),
    bucketFormat: "MMMM YYYY",
    tickFormat: "MMM YYYY",
    slotFormat: "YYYY-MM",
    calendarUnit: "month",
    maxRange: { amount: 5, unit: "year" },
    retention: { amount: 5, unit: "year" },
  },
  [YEAR]: {
    unit: "year",
    label: m.consumption_view_year,
    averageLabel: m.consumption_average_per_year,
    count: count => m.year({ count }),
    bucketFormat: "YYYY",
    tickFormat: "YYYY",
    slotFormat: "YYYY",
    calendarUnit: "year",
    maxRange: { amount: 50, unit: "year" },
    retention: { amount: 50, unit: "year" },
  },
};

const GRANULARITY_ORDER = [MINUTE, HOUR, DAY, MONTH, YEAR] as const;

const SPAN_COUNTS: Record<CalendarSpan["unit"], (count: number) => string> = {
  day: count => m.day({ count }),
  month: count => m.month({ count }),
  year: count => m.year({ count }),
};

const PERIOD_PRESETS: Record<PeriodPreset, PresetSettings> = {
  [PeriodPreset.SEVEN_DAYS]: { span: { amount: 7, unit: "day" }, granularity: HOUR },
  [PeriodPreset.THREE_MONTHS]: { span: { amount: 3, unit: "month" }, granularity: DAY },
  [PeriodPreset.TWELVE_MONTHS]: { span: { amount: 12, unit: "month" }, granularity: MONTH },
  [PeriodPreset.FIVE_YEARS]: { span: { amount: 5, unit: "year" }, granularity: YEAR },
};

const DEFAULT_PERIOD_PRESET = PeriodPreset.TWELVE_MONTHS;

const DEFAULT_GRANULARITY = MONTH;
const MIN_BUCKETS = 2;
const DEFAULT_MAX_BUCKETS = 200;

const WEEK_DAYS = 7;

const COMPARISONS: Record<Comparison, ComparisonSettings> = {
  [Comparison.NONE]: { label: m.consumption_comparison_none, name: m.consumption_comparison_none, objective: false },
  [Comparison.PREVIOUS_YEAR]: {
    label: () => m.consumption_comparison_previous_years({ years: 1 }),
    name: () => m.consumption_comparison_previous_years_short({ years: 1 }),
    years: 1,
    objective: true,
  },
  [Comparison.TWO_YEARS_BACK]: {
    label: () => m.consumption_comparison_previous_years({ years: 2 }),
    name: () => m.consumption_comparison_previous_years_short({ years: 2 }),
    years: 2,
    objective: true,
  },
  [Comparison.THREE_YEARS_BACK]: {
    label: () => m.consumption_comparison_previous_years({ years: 3 }),
    name: () => m.consumption_comparison_previous_years_short({ years: 3 }),
    years: 3,
    objective: true,
  },
  [Comparison.PREVIOUS_PERIOD]: {
    label: m.consumption_comparison_previous_period,
    name: m.consumption_comparison_previous_period,
    objective: false,
  },
  [Comparison.REFERENCE]: {
    label: m.consumption_comparison_reference,
    name: m.consumption_comparison_reference_average,
    granularities: [MONTH],
    objective: true,
  },
};

const DEGREE_DAYS_GRANULARITY = MONTH;

const OBJECTIVE_PERCENT = { min: 5, max: 50, step: 5, default: 10 };

const CHART_SERIES: Record<SeriesKey, Omit<ChartSeries, "key" | "label">> = {
  [SeriesKey.CONSUMPTION]: { color: colors.chart.blue, kind: "bar", fadesInProgress: true },
  [SeriesKey.CONSUMPTION_DJU]: {
    color: colors.chart.sky,
    kind: "bar",
    fadesInProgress: true,
    replaces: SeriesKey.CONSUMPTION,
  },
  [SeriesKey.COMPARISON]: { color: colors.chart.yellow, kind: "bar" },
  [SeriesKey.COMPARISON_DJU]: { color: colors.chart.orange, kind: "bar", replaces: SeriesKey.COMPARISON },
  [SeriesKey.OBJECTIVE]: { color: colors.chart.teal, kind: "level" },
  [SeriesKey.OBJECTIVE_DJU]: { color: colors.chart.indigo, kind: "level", replaces: SeriesKey.OBJECTIVE },
};

const BASELOAD_COLOR = colors.primary.darkBlue;
const BASELOAD_GRANULARITIES: ConsumptionGranularity[] = [HOUR, DAY];

const UNALLOCATED_KEY = "unallocated";
const UNALLOCATED_COLOR = colors.neutral[400];
const UNALLOCATED_ALERT_SHARE = 0.15;
const SUB_METERS_STACK_ID = "sub-meters";

export {
  BASELOAD_COLOR,
  BASELOAD_GRANULARITIES,
  CHART_SERIES,
  COMPARISONS,
  CONSUMPTION_TABS,
  DEFAULT_GRANULARITY,
  DEFAULT_MAX_BUCKETS,
  DEFAULT_PERIOD_PRESET,
  DEGREE_DAYS_GRANULARITY,
  ENERGIES,
  ENERGY_ORDER,
  GRANULARITIES,
  GRANULARITY_ORDER,
  MIN_BUCKETS,
  OBJECTIVE_PERCENT,
  PERIOD_PRESETS,
  SPAN_COUNTS,
  SUB_METERS_STACK_ID,
  UNALLOCATED_ALERT_SHARE,
  UNALLOCATED_COLOR,
  UNALLOCATED_KEY,
  UNIT_SYMBOLS,
  WEEK_DAYS,
};
